import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import HTMLReactParser from 'html-react-parser'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { Checkbox } from '@material-ui/core'
import { Button, FlexContainer, Paragraph, RadioInput, SelectInput, TextInput } from 'heliocor-ui'

import { db } from '../api/db'
import { CommonTitle, WarningBox } from '../common'

export const YapilyStep = ({ errorHandler, errors }) => {

    const consentId = useSelector(store => store.party.consent)
    const customFields = useSelector(store => store.settings.customFields)
    const name = useSelector(store => store.party.data.name + " " + store.party.data.surname) 

    const { t } = useTranslation()

    const [link, setLink] = useState(null)
    const [countries, setCountries] = useState([])
    const [institutions, setInstitutions] = useState([])
    const [countryInstitutions, setCountryInstitutions] = useState([])
    const [selectedCountry, setSelectedCountry] = useState(null)
    const [selectedBank, setSelectedBank] = useState(null)
    const [loadingState, setLoadingState] = useState('NOT_LOADED')
    const [isLoadingLink, setIsLoadingLink] = useState(null)
    const [openTerms, setOpenTerms] = useState(false)
    const [acceptedTerms, setAcceptedTerms] = useState()
    const [finished, setFinished] = useState(false)
    const [consentChecked, setConsentChecked] = useState(false)
    const [notFound, setNotFound] = useState(false)
    const [notFoundInstitution, setNotFoundInstitution] = useState()
    const [notFoundCountry, setNotFoundCountry] = useState()

    const terms = '<p>Accept please</p>'

    //CALLBACKS
    const getInstitutions = useCallback(() => {
        db('get', 'dokstor-cafe-api/api/self-onboarding/open-banking/institutions')
            .then(res => {
                if (res) {
                    setInstitutions(res.countries)
                    setCountries(res.countries.map(c => ({ label: c.name, value: c.countryCode })))
                    setLoadingState('LOADED')
                }
            })
    }, [])

    const getLink = useCallback(() => {
        setIsLoadingLink(true)
        db('post', 'dokstor-cafe-api/api/self-onboarding/open-banking/account-authorisation', { institutionId: selectedBank.value })
            .then(res => {
                if (res) {
                    setLink(res.authorizationUrl)
                    setIsLoadingLink(false)
                }
            })
    }, [selectedBank])

    const sendProperties = useCallback((properties) => {
        db('post', 'dokstor-cafe-api/api/self-onboarding/persons/open-banking/properties', { properties })
            .then(res => {
                if (res) {
                    setFinished(true)
                }
            })
    }, [])

    const checkConsent = useCallback(() => {
        db('get', 'dokstor-cafe-api/api/self-onboarding/persons/open-banking/consent-id/exists')
            .then(res => {
                if (res) {
                    setConsentChecked(true)
                    setFinished(res.exists)
                }
            })
    }, [])

    const onLinkClick = () => window.location.href = link

   /*  const getExpiringDate = () => moment()
        .add(90, 'days')
        .format('DD/MM/YYYY') */
    
    const countriesList = useMemo(() => {
        let countries = []
        for (const cat in customFields) {
            countries = customFields[cat].find(f => f.id === 'nationalities')?.customOptions
            if (countries) break
        }
        return countries
    }, [customFields])

    //EFFECTS
    //Get all countries with their institutions after accepting terms and manage next button
    useEffect(() => {
        if ((acceptedTerms !== 'no' && !finished) || (!selectedBank && !finished)) {
            errorHandler({yapily: ['waiting terms acceptance']})
        } else {
            errorHandler({ yapily: [] })
        }
        return () => errorHandler({ yapily: [] })
    }, [acceptedTerms, errorHandler, finished, selectedBank])

    //Get all countries after mount
    useEffect(() => {
        if (loadingState === 'NOT_LOADED') {
            setLoadingState('LOADING')
            getInstitutions()
            errorHandler({ yapily: ['waiting consent'] })
        }
    }, [loadingState, getInstitutions, errorHandler])

    //Set country institutions after selecting country
    useEffect(() => {
        if (selectedCountry) {
            if (countryInstitutions.length > 0) setCountryInstitutions([])
            setCountryInstitutions(institutions.find(c => c.countryCode === selectedCountry.value).institutions)
        }
    }, [countryInstitutions.length, setCountryInstitutions, institutions, selectedCountry])

    //Get yapily url when we select institution
    useEffect(() => {
        if (selectedBank) getLink()
    }, [selectedBank, getLink])

    //Send consent ID
    useEffect(() => {
        if (consentId && consentChecked && !finished) sendProperties({CONSENT_ID: consentId})
    }, [consentId, consentChecked, finished, sendProperties])

    //Check consent ID
    useEffect(() => {
        checkConsent()
    }, [checkConsent])

    return consentId || finished
        ? <>
            <CommonTitle value='Account Validation' />
            <FlexContainer justifyContent='center' margin='200px 0 0'>
                <ParagraphTerms>Thank you for verifying your bank information! You're all set to proceed to the next step.</ParagraphTerms>
            </FlexContainer>
        </>
        : <>
            <CommonTitle value='Bank Account Validation' />
            <FlexContainer justifyContent='center' alignItems='center'>
                <ParagraphTerms>
                    <Paragraph>Almost there! We just need to verify your bank account via connecting to Yapily Connect to verify your bank information.</Paragraph>
                    <Paragraph>Please, select the country and bank associated with your account.</Paragraph>
                    <SelectInput
                        id='groupId'
                        customLabel={t("country")}
                        options={countries}
                        onChange={(x, option) => setSelectedCountry(option)}
                        value={selectedCountry}
                        isLoading={loadingState === 'LOADING'}
                    />
                    <SelectInput
                        id='groupId'
                        customLabel={t("Bank")}
                        options={countryInstitutions.map(b => ({ label: b.name, value: b.id }))}
                        onChange={(x, option) => setSelectedBank(option)}
                        value={selectedBank}
                        disabled={!selectedCountry}
                    />
                </ParagraphTerms>
                <WarningBox
                    open={openTerms}
                    title={'termsAndConditions'}
                    onClose={() => setOpenTerms(false)}
                    content={<div className='html-parser'>{HTMLReactParser(terms)}</div>}
                    buttons={!acceptedTerms && [{
                        value: t('button.accept'),
                        onClick: () => setAcceptedTerms('yes') & setOpenTerms(false)
                    }]}
                />
            </FlexContainer>

            {selectedBank &&
                <FlexContainer justifyContent='center' column>
                    <ParagraphTerms>
                        <Paragraph>
                            We have partnered with Yapily Connect to access your bank data at {selectedBank.label}.
                        </Paragraph>
                        <Paragraph>
                            You will now be securely redirected to {selectedBank.label} to give access to the following information: Your full name ({name})
                        </Paragraph>
                        <Paragraph>
                            By using the service, you agree to Yapily Connect accessing your bank data, the&nbsp;
                            <span onClick={() => window.open("https://www.yapily.com/legal/end-user-terms")}>
                                <strong style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                    Terms & Conditions
                                </strong>
                            </span>
                            &nbsp;and&nbsp;
                            <span onClick={() => window.open("https://www.yapily.com/legal/privacy-policy")}>
                                <strong style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                    Privacy Notice
                                </strong>
                            </span>
                            .
                        </Paragraph>
                        <Paragraph>
                            This consent will be valid until {/* getExpiringDate() */}.
                        </Paragraph>
                        <FlexContainer justifyContent='center'>
                            <RadioInput
                                color='primary'
                                value={acceptedTerms}
                                onChange={(id, value) => setAcceptedTerms(value)}
                                options={[{ label: 'Confirm', value: 'yes' }, { label: 'Cancel', value: 'no' }]}
                                errorHandler={errorHandler}
                                t={t}
                            />
                        </FlexContainer>
                        <FlexContainer justifyContent='center'>
                            <Button
                                value='Verify'
                                disabled={!link || acceptedTerms !== 'yes'}
                                margin='10px 10px 0 0'
                                onClick={onLinkClick}
                                type={'default'}
                                isLoading={isLoadingLink}
                            />
                        </FlexContainer>
                    </ParagraphTerms>
                </FlexContainer>
            }
            {!selectedBank && <ParagraphTerms>
                <FlexContainer margin='0 0 0 -15px'>
                    <Checkbox
                        color='primary'
                        onChange={() => setNotFound(!notFound)}
                        checked={notFound}
                        disabled={!!selectedBank}
                    />
                    <Paragraph margin='15px 0 0'>
                        I couldn't locate my bank in the dropdown list
                    </Paragraph>
                </FlexContainer>
                {notFound &&
                    <FlexContainer column>
                        <SelectInput
                            id='notFoundCountry'
                            customLabel={"Please, provide your country"}
                            options={countriesList}
                            onChange={(_, value ) => setNotFoundCountry(value)}
                            value={notFoundCountry}
                            validations={['REQUIRED']}
                            errorHandler={errorHandler}
                            errors={errors.notFoundCountry}
                            t={t}
                        />
                        <TextInput
                            id="notFoundInstitution"
                            customLabel='Please, provide your bank name'
                            onChange={(_, value ) => setNotFoundInstitution(value)}
                            value={notFoundInstitution}
                            errorHandler={errorHandler}
                            disabled={!notFoundCountry}
                            validations={['REQUIRED']}
                            errors={errors.notFoundInstitution}
                            t={t}
                        />
                        <FlexContainer justifyContent='center'>
                            <Button
                                value='Send'
                                disabled={!notFoundInstitution}
                                margin='10px 10px 0 0'
                                onClick={() => sendProperties({
                                    NOT_SUPPORTED_BANK_COUNTRY: notFoundCountry.value,
                                    NOT_SUPPORTED_BANK_NAME: notFoundInstitution
                                })}
                                type={'default'}
                            />
                        </FlexContainer>
                    </FlexContainer>
                }
            </ParagraphTerms>}
        </>
}

const ParagraphTerms = styled(Paragraph)`
    margin: 0;
    padding-left: 48px;
    padding-right: 48px;
`